import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { NewsletterTemplate, ContentStructure } from "../types";
import { toast } from "sonner";
import { Json } from "@/integrations/supabase/types/common";

export const useTemplateCreation = () => {
  const queryClient = useQueryClient();

  const createTemplateMutation = useMutation({
    mutationFn: async (data: Partial<NewsletterTemplate>) => {
      const contentStructure = data.content_structure as unknown as Json;
      
      const { data: template, error } = await supabase
        .from('newsletter_templates')
        .insert({
          name: data.name,
          template_type: data.template_type,
          content_structure: contentStructure,
          user_category: data.user_category,
          description: data.description,
        })
        .select()
        .single();

      if (error) throw error;
      return template;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsletter-templates'] });
      toast.success('Template created successfully');
    },
    onError: (error) => {
      console.error('Error creating template:', error);
      toast.error('Failed to create template');
    }
  });

  const createVersionMutation = useMutation({
    mutationFn: async ({ templateId, version }: { templateId: string; version: Partial<NewsletterTemplate> }) => {
      const contentStructure = version.content_structure as unknown as Json;
      
      const { data: templateVersion, error } = await supabase
        .from('newsletter_template_versions')
        .insert({
          template_id: templateId,
          version_number: 1, // Default to version 1 for new templates
          content_structure: contentStructure,
        })
        .select()
        .single();

      if (error) throw error;
      return templateVersion;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsletter-templates'] });
      toast.success('Template version created successfully');
    },
    onError: (error) => {
      console.error('Error creating template version:', error);
      toast.error('Failed to create template version');
    }
  });

  return {
    createTemplateMutation,
    createVersionMutation
  };
};